import { forwardRef, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { getOverlayColor } from '@elfsight/lib-utils';
import { absoluteFitMixin, createReadableClass } from '@elfsight/widget-utils';
import canAutoPlay from 'can-autoplay';
import { isIOS } from 'react-device-detect';
import { BackgroundVideo } from './BackgroundVideo';
import { getGradientValue } from './get-gradient-value';
import type { BackgroundProps, BackgroundSize } from './types';
import { useBackgroundSize } from './useBackgroundSize';

export const Background = forwardRef<HTMLDivElement, BackgroundProps>(
  (
    {
      colorAndImageBlockRef = null,
      type = 'color',
      color,
      borderRadius,
      gradient = {},
      imageBrightness,
      imageFixOnScroll,
      imageFile,
      videoUrl,
      videoScale,
      videoBrightness,
      videoFixOnScroll,
      videoOffsetTop,
      videoPlaceholderImage,
      style,
      colorAndImageBlockStyles,
      imageFittingMode = 'fill'
    },
    forwardingRef
  ) => {
    const [videoCanAutoplay, setVideoCanAutoplay] = useState(false);
    const shouldFixBackgroundImage = imageFixOnScroll && type === 'image';

    const backgroundSize = useBackgroundSize({
      targetRef: colorAndImageBlockRef,
      imageFittingMode,
      imageDimensions: {
        width: imageFile?.width ?? 0,
        height: imageFile?.height ?? 0
      }
    });

    useEffect(() => {
      if (type === 'video') {
        checkAutoplay();

        async function checkAutoplay() {
          const { result } = await canAutoPlay.video();
          setVideoCanAutoplay(result);
        }
      }
    }, [type]);

    const backgroundImage = useMemo(
      () =>
        ({
          gradient: getGradientValue(gradient),
          image: (() => {
            if (!imageFile?.url) {
              return undefined;
            }

            return `url(${imageFile.url})`;
          })(),
          color: undefined,
          video: undefined
        })[type],
      [type, gradient, imageFile]
    );

    const overlayColor = useMemo(
      () =>
        getOverlayColor(
          {
            image: imageBrightness,
            video: videoBrightness,
            color: undefined,
            gradient: undefined
          }[type]
        ),
      [imageBrightness, videoBrightness, type]
    );

    return (
      <Container ref={forwardingRef} style={{ ...style, borderRadius }}>
        <Base
          ref={colorAndImageBlockRef}
          style={{
            backgroundImage,
            // display: block ставится через inline стили, чтоб решить конфликт с Shopify
            display: 'block',
            backgroundColor: color,
            backgroundAttachment: shouldFixBackgroundImage
              ? 'fixed'
              : undefined,
            ...(type === 'image' ? colorAndImageBlockStyles : {})
          }}
          _backgroundSize={backgroundSize}
          _shouldUseFixedBackgroundOnIOS={!!shouldFixBackgroundImage && isIOS}
        />

        {type === 'video' && videoUrl && (
          <BackgroundVideo
            {...videoUrl}
            fixOnScroll={videoFixOnScroll}
            scale={videoScale}
            offsetTop={videoOffsetTop}
          />
        )}

        <Overlay
          style={{
            // display: block ставится через inline стили, чтоб решить конфликт с Shopify
            display: 'block',
            backgroundColor: overlayColor,
            backgroundImage:
              type === 'video' && !videoCanAutoplay
                ? `url(${videoPlaceholderImage})`
                : undefined
          }}
        />
      </Container>
    );
  }
);

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'background',
    element: 'container'
  })
})`
  ${absoluteFitMixin};
  border-radius: inherit;
  overflow: hidden;
`;

const Base = styled.div.attrs<{
  _backgroundSize: BackgroundSize;
  _shouldUseFixedBackgroundOnIOS: boolean;
}>({
  className: createReadableClass({
    component: 'background',
    element: 'base'
  })
})`
  ${absoluteFitMixin};
  z-index: 1;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: inherit;
  background-size: ${({ _backgroundSize }) => _backgroundSize};

  ${({ _shouldUseFixedBackgroundOnIOS }) =>
    _shouldUseFixedBackgroundOnIOS &&
    css`
      height: 100vh;
      @supports (height: 100lvh) {
        height: 100lvh;
      }

      &::before {
        content: '';
        position: fixed;
        pointer-events: none;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        @supports (height: 100lvh) {
          height: 100lvh;
        }
        background-image: inherit;
        background-size: cover;
        background-position: center;
        z-index: -999;
      }
    `};
`;

const Overlay = styled.div.attrs({
  className: createReadableClass({
    component: 'background',
    element: 'overlay'
  })
})`
  ${absoluteFitMixin};
  z-index: 2;
`;
